import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  BarChart,
  BarList,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';
import { api } from '../lib/api';
import { date, dateShort, dateTime, money, money0 } from '../lib/format';

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface PlatformDashboard {
  tenants: {
    total: number;
    trialing: number;
    active: number;
    pastDue: number;
    suspended: number;
    expired: number;
    cancelled: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  scale: { properties: number; users: number };
  revenue: { mrr: number; arr: number; payingTenants: number; averageRevenuePerTenant: number };
  planDistribution: Array<{ code: string; name: string; tenants: number; mrr: number }>;
  signupTrend: Array<{ day: string; count: number }>;
  trialsEndingSoon: Array<{
    tenantId: string;
    name: string;
    contactEmail: string | null;
    daysRemaining: number;
  }>;
  recentRegistrations: Array<{
    tenantId: string;
    name: string;
    city: string | null;
    contactEmail: string | null;
    source: string;
    status: string | null;
    properties: number;
    users: number;
    registeredAt: string;
  }>;
}

interface TenantRow {
  tenantId: string;
  name: string;
  source: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  city: string | null;
  gstin: string | null;
  propertyNames: string[];
  propertyCount: number;
  userCount: number;
  planCode: string | null;
  planName: string | null;
  priceMonthly: number;
  status: string;
  statusLabel: string;
  daysRemaining: number;
  registeredAt: string;
}

interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  trialDays: number;
  maxProperties: number;
  maxUsers: number;
  isPublic: boolean;
  isActive: boolean;
  subscribers: number;
}

/* ------------------------------------------------------------------ */
/* Dashboard                                                            */
/* ------------------------------------------------------------------ */

export function PlatformDashboardPage() {
  const query = useQuery({
    queryKey: ['platform', 'dashboard'],
    queryFn: () => api.get<PlatformDashboard>('/api/platform/dashboard'),
  });

  if (query.isLoading) return <Loading label="Loading the platform…" />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const data = query.data!;

  return (
    <div className="stack">
      <div className="kpis">
        <Kpi
          label="Hotels registered"
          value={String(data.tenants.total)}
          foot={`${data.tenants.newThisWeek} this week · ${data.tenants.newThisMonth} this month`}
        />
        <Kpi
          label="Monthly recurring revenue"
          value={money0(data.revenue.mrr)}
          foot={`${data.revenue.payingTenants} paying · ${money0(data.revenue.arr)} annualised`}
          tone="blue"
        />
        <Kpi
          label="On trial"
          value={String(data.tenants.trialing)}
          foot={`${data.trialsEndingSoon.length} ending within a week`}
          tone={data.trialsEndingSoon.length ? 'amber' : ''}
        />
        <Kpi
          label="Needs attention"
          value={String(data.tenants.suspended + data.tenants.expired)}
          foot={`${data.tenants.suspended} suspended · ${data.tenants.expired} expired`}
          tone={data.tenants.suspended + data.tenants.expired ? 'red' : ''}
        />
        <Kpi
          label="Properties"
          value={String(data.scale.properties)}
          foot={`${data.scale.users} hotel users`}
        />
        <Kpi
          label="Average revenue"
          value={money0(data.revenue.averageRevenuePerTenant)}
          foot="Per paying hotel, monthly"
        />
      </div>

      <div className="grid-32">
        <div className="card">
          <div className="card-head">
            <h2>Signups, last 30 days</h2>
            <span className="chip">{data.tenants.newThisMonth} total</span>
          </div>
          <div className="card-body">
            <BarChart
              data={data.signupTrend.map((d) => ({ label: dateShort(d.day), value: d.count }))}
              height={190}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Plan mix</h2>
          </div>
          <div className="card-body">
            <BarList
              rows={data.planDistribution.map((p) => ({
                label: `${p.name} · ${p.tenants} ${p.tenants === 1 ? 'hotel' : 'hotels'}`,
                value: p.mrr,
              }))}
              format={money0}
            />
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Latest registrations</h2>
          </div>
          {data.recentRegistrations.length === 0 ? (
            <Empty message="No hotels have registered yet" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Hotel</th>
                    <th>City</th>
                    <th>Contact</th>
                    <th>Registered</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentRegistrations.map((row) => (
                    <tr key={row.tenantId}>
                      <td>
                        <div className="b">{row.name}</div>
                        <div className="xs muted">
                          {row.properties} property · {row.users} users
                        </div>
                      </td>
                      <td className="dim">{row.city ?? '—'}</td>
                      <td className="small">{row.contactEmail ?? '—'}</td>
                      <td className="nowrap">{date(row.registeredAt)}</td>
                      <td>{row.status ? <StatusChip status={row.status} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Trials ending soon</h2>
          </div>
          {data.trialsEndingSoon.length === 0 ? (
            <Empty icon="✓" message="No trial ends in the next seven days" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Hotel</th>
                    <th>Contact</th>
                    <th className="num">Days left</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trialsEndingSoon.map((row) => (
                    <tr key={row.tenantId}>
                      <td className="b">{row.name}</td>
                      <td className="small">{row.contactEmail ?? '—'}</td>
                      <td className="num">
                        <span className={`chip ${row.daysRemaining <= 2 ? 'danger' : 'warn'}`}>
                          {row.daysRemaining}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hotels                                                               */
/* ------------------------------------------------------------------ */

export function PlatformHotelsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [openTenant, setOpenTenant] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['platform', 'tenants', q, status],
    queryFn: () =>
      api.get<{ total: number; entries: TenantRow[] }>(
        `/api/platform/tenants?${new URLSearchParams({
          limit: '200',
          ...(q ? { q } : {}),
          ...(status ? { status } : {}),
        }).toString()}`,
      ),
  });

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div className="row gap-6">
            <div className="search" style={{ width: 260 }}>
              <input
                className="input"
                placeholder="Hotel, city or contact email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ width: 170 }}
            >
              <option value="">Every status</option>
              <option value="TRIALING">On trial</option>
              <option value="ACTIVE">Active</option>
              <option value="PAST_DUE">Payment overdue</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="EXPIRED">Expired</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
          <span className="chip">{query.data?.entries.length ?? 0} shown</span>
        </div>

        {query.isLoading ? (
          <Loading />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data!.entries.length === 0 ? (
          <Empty message="No hotels match" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th>Contact</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th className="num">Days left</th>
                  <th className="num">Scale</th>
                  <th>Registered</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {query.data!.entries.map((row) => (
                  <tr
                    key={row.tenantId}
                    className="clickable"
                    onClick={() => setOpenTenant(row.tenantId)}
                  >
                    <td>
                      <div className="b">{row.name}</div>
                      <div className="xs muted">
                        {row.city ?? '—'}
                        {row.gstin ? ` · ${row.gstin}` : ''}
                        {row.source === 'SELF_SERVICE' ? ' · self-registered' : ''}
                      </div>
                    </td>
                    <td className="small">
                      {row.contactEmail ?? '—'}
                      {row.contactPhone ? <div className="xs muted">{row.contactPhone}</div> : null}
                    </td>
                    <td>
                      <div className="b">{row.planName ?? '—'}</div>
                      {row.priceMonthly > 0 ? (
                        <div className="xs muted">{money0(row.priceMonthly)}/mo</div>
                      ) : null}
                    </td>
                    <td>
                      <StatusChip status={row.status} />
                    </td>
                    <td className="num">{row.daysRemaining || '—'}</td>
                    <td className="num dim">
                      {row.propertyCount}p · {row.userCount}u
                    </td>
                    <td className="nowrap">{date(row.registeredAt)}</td>
                    <td className="right">
                      <button
                        className="btn ghost sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTenant(row.tenantId);
                        }}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openTenant ? (
        <TenantDrawer tenantId={openTenant} onClose={() => setOpenTenant(null)} />
      ) : null}
    </div>
  );
}

interface TenantDetail {
  tenantId: string;
  name: string;
  source: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  registeredAt: string;
  properties: Array<{
    id: string;
    name: string;
    code: string;
    city: string;
    email: string;
    phone: string;
    gstin: string;
    rooms: number;
    employees: number;
  }>;
  users: Array<{
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    lastLoginAt: string | null;
    roles: string[];
  }>;
  usage: { invoices: number; lastInvoiceAt: string | null };
  subscription: {
    planCode: string;
    planName: string;
    priceMonthly: number;
    status: string;
    statusLabel: string;
    daysRemaining: number;
    endsAt: string | null;
    events: Array<{
      id: string;
      type: string;
      fromPlanCode: string | null;
      toPlanCode: string | null;
      note: string | null;
      createdAt: string;
    }>;
  } | null;
}

function TenantDrawer({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [action, setAction] = useState('CHANGE_PLAN');
  const [planCode, setPlanCode] = useState('SILVER');
  const [days, setDays] = useState(15);
  const [months, setMonths] = useState(1);
  const [note, setNote] = useState('');

  const detail = useQuery({
    queryKey: ['platform', 'tenant', tenantId],
    queryFn: () => api.get<TenantDetail>(`/api/platform/tenants/${tenantId}`),
  });
  const plans = useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: () => api.get<Plan[]>('/api/platform/plans'),
  });

  const act = useMutation({
    mutationFn: () =>
      api.post(`/api/platform/tenants/${tenantId}/subscription`, {
        action,
        ...(action === 'CHANGE_PLAN' ? { planCode } : {}),
        ...(action === 'EXTEND_TRIAL' ? { days } : {}),
        ...(action === 'EXTEND_PLAN' || action === 'ACTIVATE' ? { months } : {}),
        ...(note ? { note } : {}),
      }),
    onSuccess: () => {
      push('Subscription updated', 'ok');
      setNote('');
      void client.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: fail,
  });

  return (
    <Modal title={detail.data?.name ?? 'Hotel'} onClose={onClose} size="wide">
      {detail.isLoading ? (
        <Loading />
      ) : detail.error ? (
        <ErrorState error={detail.error} />
      ) : (
        <div className="stack">
          <div className="kpis">
            <Kpi
              label="Plan"
              value={detail.data!.subscription?.planName ?? '—'}
              foot={
                detail.data!.subscription
                  ? `${money0(detail.data!.subscription.priceMonthly)}/month`
                  : ''
              }
            />
            <Kpi
              label="Status"
              value={detail.data!.subscription?.statusLabel ?? '—'}
              foot={
                detail.data!.subscription?.endsAt
                  ? `until ${date(detail.data!.subscription.endsAt)}`
                  : ''
              }
            />
            <Kpi
              label="Invoices raised"
              value={String(detail.data!.usage.invoices)}
              foot={
                detail.data!.usage.lastInvoiceAt
                  ? `last ${dateShort(detail.data!.usage.lastInvoiceAt)}`
                  : 'never billed'
              }
              tone={detail.data!.usage.invoices === 0 ? 'amber' : ''}
            />
            <Kpi
              label="Registered"
              value={dateShort(detail.data!.registeredAt)}
              foot={
                detail.data!.source === 'SELF_SERVICE' ? 'Self-registered' : 'Created by Kortex'
              }
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Contact</h3>
            </div>
            <div className="card-body small">
              <div>{detail.data!.contactName ?? '—'}</div>
              <div className="muted">
                {detail.data!.contactEmail ?? '—'} · {detail.data!.contactPhone ?? '—'}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Properties</h3>
            </div>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>City</th>
                    <th>GSTIN</th>
                    <th>Email</th>
                    <th className="num">Rooms</th>
                    <th className="num">Staff</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data!.properties.map((p) => (
                    <tr key={p.id}>
                      <td className="b">
                        {p.name} <span className="xs muted mono">{p.code}</span>
                      </td>
                      <td>{p.city}</td>
                      <td className="mono xs">{p.gstin}</td>
                      <td className="small">{p.email}</td>
                      <td className="num">{p.rooms}</td>
                      <td className="num">{p.employees}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Manage subscription</h3>
            </div>
            <div className="card-body col gap-16">
              <div className="form-grid three">
                <Select label="Action" value={action} onChange={(e) => setAction(e.target.value)}>
                  <option value="CHANGE_PLAN">Change plan</option>
                  <option value="EXTEND_TRIAL">Extend trial</option>
                  <option value="EXTEND_PLAN">Extend plan</option>
                  <option value="ACTIVATE">Activate</option>
                  <option value="SUSPEND">Suspend</option>
                  <option value="CANCEL">Cancel</option>
                </Select>
                {action === 'CHANGE_PLAN' ? (
                  <Select
                    label="Plan"
                    value={planCode}
                    onChange={(e) => setPlanCode(e.target.value)}
                  >
                    {(plans.data ?? []).map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name} — {p.priceMonthly === 0 ? 'free' : `${money0(p.priceMonthly)}/mo`}
                      </option>
                    ))}
                  </Select>
                ) : null}
                {action === 'EXTEND_TRIAL' ? (
                  <TextInput
                    label="Days to add"
                    type="number"
                    min={1}
                    max={365}
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                  />
                ) : null}
                {action === 'EXTEND_PLAN' || action === 'ACTIVATE' ? (
                  <TextInput
                    label="Months"
                    type="number"
                    min={1}
                    max={36}
                    hint={
                      action === 'EXTEND_PLAN'
                        ? 'Added to whatever is left, not from today'
                        : 'From today'
                    }
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                  />
                ) : null}
                <TextInput
                  label="Note"
                  placeholder="Why"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <button className="btn primary" disabled={act.isPending} onClick={() => act.mutate()}>
                {act.isPending ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Subscription history</h3>
            </div>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Plan</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.data!.subscription?.events ?? []).map((e) => (
                    <tr key={e.id}>
                      <td className="nowrap">{dateTime(e.createdAt)}</td>
                      <td>
                        <span className="chip">{e.type.replace(/_/g, ' ').toLowerCase()}</span>
                      </td>
                      <td className="small">
                        {e.fromPlanCode && e.fromPlanCode !== e.toPlanCode
                          ? `${e.fromPlanCode} → ${e.toPlanCode}`
                          : (e.toPlanCode ?? '—')}
                      </td>
                      <td className="small dim">{e.note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Plans                                                                */
/* ------------------------------------------------------------------ */

export function PlatformPlansPage() {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [editing, setEditing] = useState<Plan | null>(null);

  const query = useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: () => api.get<Plan[]>('/api/platform/plans'),
  });

  const save = useMutation({
    mutationFn: (plan: Plan) =>
      api.patch(`/api/platform/plans/${plan.id}`, {
        name: plan.name,
        description: plan.description ?? '',
        priceMonthly: Number(plan.priceMonthly),
        trialDays: Number(plan.trialDays),
        maxProperties: Number(plan.maxProperties),
        maxUsers: Number(plan.maxUsers),
        isPublic: plan.isPublic,
        isActive: plan.isActive,
      }),
    onSuccess: () => {
      push('Plan updated', 'ok');
      setEditing(null);
      void client.invalidateQueries({ queryKey: ['platform', 'plans'] });
    },
    onError: fail,
  });

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>Subscription plans</h2>
            <div className="small muted">
              What a hotel sees on the registration page, and what it is charged.
            </div>
          </div>
        </div>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Plan</th>
                <th className="num">Price / month</th>
                <th className="num">Free trial</th>
                <th className="num">Limits</th>
                <th className="num">Hotels</th>
                <th>Visibility</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {query.data!.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <div className="b">
                      {plan.name} <span className="xs muted mono">{plan.code}</span>
                    </div>
                    {plan.description ? <div className="xs muted">{plan.description}</div> : null}
                  </td>
                  <td className="num b">
                    {plan.priceMonthly === 0 ? 'Free' : money(plan.priceMonthly)}
                  </td>
                  <td className="num">{plan.trialDays ? `${plan.trialDays} days` : '—'}</td>
                  <td className="num dim">
                    {plan.maxProperties}p · {plan.maxUsers}u
                  </td>
                  <td className="num">{plan.subscribers}</td>
                  <td>
                    <span className={`chip ${plan.isActive ? 'ok' : 'danger'}`}>
                      {plan.isActive ? 'Active' : 'Retired'}
                    </span>
                    {plan.isPublic ? (
                      <span className="chip info" style={{ marginLeft: 4 }}>
                        Public
                      </span>
                    ) : null}
                  </td>
                  <td className="right">
                    <button className="btn ghost sm" onClick={() => setEditing({ ...plan })}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <Modal
          title={`Edit ${editing.name}`}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button className="btn" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={save.isPending}
                onClick={() => save.mutate(editing)}
              >
                {save.isPending ? 'Saving…' : 'Save plan'}
              </button>
            </>
          }
        >
          <div className="form-grid">
            <TextInput
              label="Name"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            />
            <TextInput
              label="Price per month (₹)"
              type="number"
              min={0}
              value={editing.priceMonthly}
              onChange={(e) => setEditing({ ...editing, priceMonthly: Number(e.target.value) })}
            />
            <TextInput
              label="Free trial (days)"
              type="number"
              min={0}
              max={365}
              hint="Applies to hotels registering from now on."
              value={editing.trialDays}
              onChange={(e) => setEditing({ ...editing, trialDays: Number(e.target.value) })}
            />
            <TextInput
              label="Max properties"
              type="number"
              min={1}
              value={editing.maxProperties}
              onChange={(e) => setEditing({ ...editing, maxProperties: Number(e.target.value) })}
            />
            <TextInput
              label="Max users"
              type="number"
              min={1}
              value={editing.maxUsers}
              onChange={(e) => setEditing({ ...editing, maxUsers: Number(e.target.value) })}
            />
            <div className="span2">
              <TextInput
                label="Description"
                value={editing.description ?? ''}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </div>
            <label className="row gap-6">
              <input
                type="checkbox"
                checked={editing.isPublic}
                onChange={(e) => setEditing({ ...editing, isPublic: e.target.checked })}
              />
              Offered on the registration page
            </label>
            <label className="row gap-6">
              <input
                type="checkbox"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reports                                                              */
/* ------------------------------------------------------------------ */

export function PlatformReportsPage() {
  const [days, setDays] = useState(30);

  const signups = useQuery({
    queryKey: ['platform', 'signups', days],
    queryFn: () =>
      api.get<{
        total: number;
        byDay: Array<{ day: string; count: number }>;
        bySource: Array<{ source: string; count: number }>;
      }>(`/api/platform/reports/signups?days=${days}`),
  });
  const revenue = useQuery({
    queryKey: ['platform', 'revenue'],
    queryFn: () =>
      api.get<{
        mrr: number;
        arr: number;
        byPlan: Array<{ code: string; name: string; tenants: number; paying: number; mrr: number }>;
        funnel: { trialing: number; converted: number; conversionPct: number; churned: number };
      }>('/api/platform/reports/revenue'),
  });
  const usage = useQuery({
    queryKey: ['platform', 'usage'],
    queryFn: () =>
      api.get<{
        dormantCount: number;
        entries: Array<{
          tenantId: string;
          name: string;
          planCode: string | null;
          status: string;
          users: number;
          properties: number;
          rooms: number;
          invoices30d: number;
          lastInvoiceAt: string | null;
          dormant: boolean;
        }>;
      }>('/api/platform/reports/usage'),
  });

  if (signups.isLoading || revenue.isLoading || usage.isLoading)
    return <Loading label="Building reports…" />;

  return (
    <div className="stack">
      <div className="row between wrap gap-6">
        <div className="seg">
          {[7, 30, 90].map((d) => (
            <button key={d} className={d === days ? 'active' : ''} onClick={() => setDays(d)}>
              Last {d} days
            </button>
          ))}
        </div>
        <button className="btn no-print" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div className="kpis">
        <Kpi
          label="Signups"
          value={String(signups.data!.total)}
          foot={`in the last ${days} days`}
        />
        <Kpi
          label="MRR"
          value={money0(revenue.data!.mrr)}
          foot={`${money0(revenue.data!.arr)} annualised`}
          tone="blue"
        />
        <Kpi
          label="Trial conversion"
          value={`${revenue.data!.funnel.conversionPct}%`}
          foot={`${revenue.data!.funnel.converted} converted · ${revenue.data!.funnel.trialing} on trial`}
        />
        <Kpi
          label="Dormant hotels"
          value={String(usage.data!.dormantCount)}
          foot="Registered but never billed"
          tone={usage.data!.dormantCount ? 'amber' : ''}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Registrations over time</h2>
        </div>
        <div className="card-body">
          <BarChart
            data={signups.data!.byDay.map((d) => ({ label: dateShort(d.day), value: d.count }))}
            height={200}
          />
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Revenue by plan</h2>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th className="num">Hotels</th>
                  <th className="num">Paying</th>
                  <th className="num">MRR</th>
                </tr>
              </thead>
              <tbody>
                {revenue.data!.byPlan.map((p) => (
                  <tr key={p.code}>
                    <td className="b">{p.name}</td>
                    <td className="num">{p.tenants}</td>
                    <td className="num">{p.paying}</td>
                    <td className="num b">{money0(p.mrr)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td className="num">{money0(revenue.data!.mrr)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Where signups come from</h2>
          </div>
          <div className="card-body">
            <BarList
              rows={signups.data!.bySource.map((s) => ({
                label: s.source === 'SELF_SERVICE' ? 'Registered themselves' : 'Created by Kortex',
                value: s.count,
              }))}
              format={(v) => String(Math.round(v))}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Usage by hotel</h2>
          <span className="small muted">
            Invoices in the last 30 days — the earliest churn signal
          </span>
        </div>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Hotel</th>
                <th>Plan</th>
                <th>Status</th>
                <th className="num">Rooms</th>
                <th className="num">Users</th>
                <th className="num">Invoices 30d</th>
                <th>Last invoice</th>
              </tr>
            </thead>
            <tbody>
              {usage.data!.entries.map((row) => (
                <tr key={row.tenantId}>
                  <td className="b">{row.name}</td>
                  <td>{row.planCode ?? '—'}</td>
                  <td>
                    <StatusChip status={row.status} />
                  </td>
                  <td className="num">{row.rooms}</td>
                  <td className="num">{row.users}</td>
                  <td className={`num ${row.dormant ? 'warn b' : ''}`}>{row.invoices30d}</td>
                  <td className="nowrap dim">
                    {row.lastInvoiceAt ? dateShort(row.lastInvoiceAt) : 'never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
